import { GALAXY_CHART_SIZE } from "./galaxy-layout";

export interface GalaxyView {
  x: number;
  y: number;
  scale: number;
}

export interface GalaxyViewportSize {
  width: number;
  height: number;
}

interface Point {
  x: number;
  y: number;
}

const EDGE_MARGIN = 16;
const MAX_MAGNIFICATION = 4;

function validSize(size: GalaxyViewportSize): boolean {
  return Number.isFinite(size.width) && Number.isFinite(size.height) &&
    size.width > 0 && size.height > 0;
}

function validPoint(point: Point): boolean {
  return Number.isFinite(point.x) && Number.isFinite(point.y);
}

function validView(view: GalaxyView): boolean {
  return validPoint(view) && Number.isFinite(view.scale) && view.scale > 0;
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value));
}

export function fitGalaxyView(size: GalaxyViewportSize): GalaxyView {
  if (!validSize(size)) return { x: 0, y: 0, scale: 1 };
  const scale = Math.min(
    Math.max(1, size.width - EDGE_MARGIN * 2) / GALAXY_CHART_SIZE.width,
    Math.max(1, size.height - EDGE_MARGIN * 2) / GALAXY_CHART_SIZE.height,
  );
  return {
    x: (size.width - GALAXY_CHART_SIZE.width * scale) / 2,
    y: (size.height - GALAXY_CHART_SIZE.height * scale) / 2,
    scale,
  };
}

function constrainAxis(position: number, content: number, viewport: number): number {
  return content <= viewport
    ? (viewport - content) / 2
    : clamp(position, viewport - content - EDGE_MARGIN, EDGE_MARGIN);
}

function constrainView(view: GalaxyView, size: GalaxyViewportSize): GalaxyView {
  const fit = fitGalaxyView(size);
  if (!validSize(size) || !validView(view)) return fit;
  const scale = clamp(view.scale, fit.scale, fit.scale * MAX_MAGNIFICATION);
  const width = GALAXY_CHART_SIZE.width * scale;
  const height = GALAXY_CHART_SIZE.height * scale;
  if (!Number.isFinite(width) || !Number.isFinite(height)) return fit;
  return {
    x: constrainAxis(view.x, width, size.width),
    y: constrainAxis(view.y, height, size.height),
    scale,
  };
}

function currentView(view: GalaxyView, size: GalaxyViewportSize): GalaxyView {
  if (!validSize(size)) return validView(view) ? view : fitGalaxyView(size);
  return constrainView(view, size);
}

export function zoomGalaxyView(
  view: GalaxyView,
  size: GalaxyViewportSize,
  factor: number,
  anchor: Point,
): GalaxyView {
  const current = currentView(view, size);
  if (!validSize(size) || !validPoint(anchor) || !Number.isFinite(factor) || factor <= 0)
    return current;
  const fit = fitGalaxyView(size);
  const scale = clamp(current.scale * factor, fit.scale, fit.scale * MAX_MAGNIFICATION);
  const ratio = scale / current.scale;
  return constrainView({
    x: anchor.x - (anchor.x - current.x) * ratio,
    y: anchor.y - (anchor.y - current.y) * ratio,
    scale,
  }, size);
}

export function panGalaxyView(
  view: GalaxyView,
  size: GalaxyViewportSize,
  delta: Point,
): GalaxyView {
  const current = currentView(view, size);
  if (!validSize(size) || !validPoint(delta)) return current;
  return constrainView({
    x: current.x + delta.x,
    y: current.y + delta.y,
    scale: current.scale,
  }, size);
}

export function revealGalaxyPoint(
  view: GalaxyView,
  size: GalaxyViewportSize,
  point: Point,
): GalaxyView {
  const current = currentView(view, size);
  if (!validSize(size) || !validPoint(point)) return current;
  const x = current.x + point.x * current.scale;
  const y = current.y + point.y * current.scale;
  return constrainView({
    x: current.x + (clamp(x, EDGE_MARGIN, Math.max(EDGE_MARGIN, size.width - EDGE_MARGIN)) - x),
    y: current.y + (clamp(y, EDGE_MARGIN, Math.max(EDGE_MARGIN, size.height - EDGE_MARGIN)) - y),
    scale: current.scale,
  }, size);
}
